<template>
  <main class="login-page">
    <div class="login-layout">
      <!-- LEFT: IMAGE -->
      <aside class="brand-panel">
        <img
          v-if="logoPath"
          :src="logoPath"
          alt="DITAP"
          class="login-illustration"
        />
      </aside>

      <!-- RIGHT: LOGIN FORM -->
      <section class="form-panel">
        <div class="login-content">
          <LoginHeader />

          <LoginAlert :errors="errors" />

          <LoginForm
            :email="form.email"
            :password="form.password"
            :remember="form.remember"
            :loading="loading"
            @update:email="form.email = $event"
            @update:password="form.password = $event"
            @update:remember="form.remember = $event"
            @submit="submit"
          />

          <LoginSecurity />
        </div>

        <div class="form-footer">
          <LoginFooter />
        </div>
      </section>
    </div>
  </main>
</template>

<script>
import { reactive, ref, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useToast } from 'vue-toastification'

import { useSeoFromI18n } from '@/composables/useSeo'

import LoginHeader from '@/components/common/LoginHeader.vue'
import LoginAlert from '@/components/common/LoginAlert.vue'
import LoginForm from '@/components/common/LoginForm.vue'
import LoginFooter from '@/components/common/LoginFooter.vue'
import LoginSecurity from '@/components/common/LoginSecurity.vue'

import { initTheme } from '@/utils/theme'
import authService from '@/services/authService'
import api from '@/services/axios'

export default {
  name: 'LoginView',

  components: {
    LoginHeader,
    LoginAlert,
    LoginForm,
    LoginFooter,
    LoginSecurity
  },

  setup() {
    const router = useRouter()
    const route = useRoute()
    const toast = useToast()

    const loading = ref(false)
    const errors = ref({})
    const logoPath = ref('')

    const form = reactive({
      email: '',
      password: '',
      remember: false
    })

    // ===== SEO meta =====
    useSeoFromI18n('meta.login', {
      robots: 'noindex, nofollow'
    })

    const loadBranding = async () => {
      try {
        const response = await api.get('/public/branding')
        const data = response?.data?.data ?? response?.data ?? {}
        logoPath.value = data.logo_url || ''
      } catch (error) {
        console.warn('Gagal memuat branding:', error)
      }
    }

    const submit = async () => {
      if (loading.value) return

      loading.value = true
      errors.value = {}

      try {
        const response = await authService.login({
          email: form.email.trim(),
          password: form.password,
          remember: form.remember
        })

        const data = response?.data ?? response
        const payload = data?.data ?? data

        const user = payload?.user
        const token = payload?.token ?? payload?.access_token

        if (!user || !token) {
          throw new Error(
            'Respons login tidak berisi data pengguna atau token.'
          )
        }

        sessionStorage.setItem('token', token)
        sessionStorage.setItem('user', JSON.stringify(user))

        await initTheme()

        toast.success('Login berhasil!')

        const defaultPath = user.role === 'admin'
          ? '/admin/dashboard'
          : '/customer/dashboard'

        const requestedPath = route.query.redirect

        const targetPath =
          typeof requestedPath === 'string' &&
          requestedPath.startsWith('/') &&
          !requestedPath.startsWith('//') &&
          !requestedPath.includes('\\')
            ? requestedPath
            : defaultPath

        await router.replace(targetPath)
      } catch (error) {
        const responseData = error?.response?.data

        if (responseData?.errors) {
          errors.value = responseData.errors
        } else {
          toast.error(
            responseData?.message ||
            error.message ||
            'Email atau password salah.'
          )
        }
      } finally {
        loading.value = false
      }
    }

    onMounted(loadBranding)

    return {
      form,
      errors,
      loading,
      logoPath,
      submit
    }
  }
}
</script>

<style scoped>
/* ============================================
   PAGE — kunci tinggi viewport, TANPA scroll
   ============================================ */
.login-page {
  --primary: #0f58ff;
  --primary-dark: #0847dc;
  --turquoise: #00cfc1;
  --ink: #13213b;
  --muted: #78849b;
  --panel-bg: #0b1b42;

  display: flex;
  align-items: center;
  justify-content: center;

  /* KUNCI: height (bukan min-height) + overflow hidden */
  height: 100vh;
  height: 100dvh;
  overflow: hidden;

  /* Padding vertikal 0 supaya tidak menambah tinggi */
  padding: 0;
  margin: 0;

  background:
    radial-gradient(
      circle at 5% 5%,
      rgba(15, 88, 255, 0.07),
      transparent 30%
    ),
    radial-gradient(
      circle at 95% 95%,
      rgba(0, 207, 193, 0.08),
      transparent 28%
    ),
    #f4f7fc;

  color: var(--ink);
  /* font-family:
    Inter,
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    sans-serif; */
}

/* ============================================
   CARD — tinggi mengikuti viewport minus margin
   ============================================ */
.login-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);

  width: calc(100% - 2 * clamp(12px, 2vw, 24px));
  max-width: 1080px;

  /* Tinggi = viewport minus margin atas+bawah.
     Dikurangi ~24px agar ada sisa napas. */
  height: calc(100dvh - clamp(24px, 4vh, 48px));
  max-height: 680px;
  min-height: 0;

  overflow: hidden;

  border: 1px solid #e2e9f4;
  border-radius: 24px;
  background: #ffffff;

  box-shadow:
    0 24px 64px rgba(25, 48, 91, 0.10),
    0 3px 12px rgba(25, 48, 91, 0.035);
}

/* ============================================
   LEFT IMAGE PANEL
   ============================================ */
.brand-panel {
  position: relative;
  min-width: 0;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  background: var(--panel-bg);

  display: flex;
  align-items: center;
  justify-content: center;
}

.login-illustration {
  display: block;

  width: 100%;
  height: 100%;
  max-width: 100%;
  max-height: 100%;

  object-fit: contain;
  object-position: center;

  padding: clamp(16px, 4vw, 40px);
  box-sizing: border-box;
}

/* ============================================
   RIGHT FORM PANEL — TANPA scroll
   ============================================ */
.form-panel {
  display: flex;
  flex-direction: column;
  justify-content: center;

  min-width: 0;
  min-height: 0;

  /* Tidak ada overflow-y: auto */
  overflow: hidden;

  padding: clamp(20px, 3vh, 36px) clamp(24px, 4vw, 56px);

  background: #ffffff;
}

.login-content {
  width: 100%;
  max-width: 360px;
  margin: auto;
}

.form-footer {
  width: 100%;
  max-width: 360px;
  margin: clamp(12px, 2vh, 24px) auto 0;
}

/* ============================================
   TABLET (≤ 1000px)
   ============================================ */
@media (max-width: 1000px) {
  .login-layout {
    grid-template-columns: minmax(0, 0.9fr) minmax(0, 1.1fr);
    max-width: 960px;
    max-height: 640px;
  }

  .form-panel {
    padding: clamp(18px, 3vh, 32px) 24px;
  }
}

/* ============================================
   MOBILE (≤ 720px)
   ============================================ */
@media (max-width: 720px) {
  .login-page {
    /* Mobile butuh scroll kalau konten panjang,
       jadi height auto + overflow visible */
    display: block;
    height: auto;
    min-height: 100dvh;
    overflow: visible;
    background: #ffffff;
  }

  .login-layout {
    display: flex;
    flex-direction: column;

    width: 100%;
    max-width: 100%;

    height: auto;
    min-height: 100dvh;
    max-height: none;

    border: 0;
    border-radius: 0;
    box-shadow: none;
  }

  .brand-panel {
    width: 100%;
    height: 200px;
    min-height: 200px;
    flex: 0 0 200px;
  }

  .login-illustration {
    width: 100%;
    height: 100%;
    min-height: 0;
    max-height: 200px;
    padding: 14px;
  }

  .form-panel {
    flex: 1;
    justify-content: flex-start;

    overflow: visible;
    padding: 28px 22px 24px;
  }

  .login-content {
    max-width: 420px;
    margin: 0 auto;
  }

  .form-footer {
    max-width: 420px;
    margin: 24px auto 0;
  }
}

/* ============================================
   SMALL MOBILE (≤ 380px)
   ============================================ */
@media (max-width: 380px) {
  .brand-panel {
    height: 170px;
    min-height: 170px;
    flex-basis: 170px;
  }

  .login-illustration {
    max-height: 170px;
    padding: 10px;
  }

  .form-panel {
    padding: 24px 16px 20px;
  }

  .login-content,
  .form-footer {
    max-width: 100%;
  }
}

/* ============================================
   ACCESSIBILITY
   ============================================ */
@media (prefers-reduced-motion: reduce) {
  *,
  *::before,
  *::after {
    scroll-behavior: auto !important;
    animation-duration: 0.01ms !important;
    transition-duration: 0.01ms !important;
  }
}
</style>